import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import apiClient, { toasterFromError } from '../../apiClient';
import { toast } from 'react-toastify';

function ConfirmationModal({ data, onCancel, onSubmit }) {
  if (!data) return null;
  return (
    <div className="modal-overlay" role="dialog" aria-modal="true">
      <div className="modal-box">
        <div className="modal-title">
          <i className="fa fa-cloud-upload-alt" style={{ color: '#0DB8DE', marginRight: 8 }} aria-hidden="true"></i>
          OTA Update Summary
        </div>
        <div className="summary-row"><span className="summary-label">Application</span><span className="summary-value">{data.appName}</span></div>
        <div className="summary-row"><span className="summary-label">Current Version</span><span className="summary-value">{data.current_version || '(new application)'}</span></div>
        <div className="summary-row"><span className="summary-label">New Version</span><span className="summary-value" style={{ color: '#27EF9F' }}>{data.appVersion}</span></div>
        <div className="summary-row"><span className="summary-label">Firmware file</span><span className="summary-value">{data.fileName}</span></div>
        {data.fileSize != null && (
          <div className="summary-row"><span className="summary-label">File size</span><span className="summary-value">{(data.fileSize / 1024).toFixed(1)} KB</span></div>
        )}
        <div className="modal-actions">
          <button type="button" className="btn-ghost" onClick={onCancel}>Cancel</button>
          <button type="button" className="btn-primary-lg" onClick={onSubmit}>
            <i className="fa fa-paper-plane" style={{ marginRight: 6 }} aria-hidden="true"></i>
            Submit OTA
          </button>
        </div>
      </div>
    </div>
  );
}

const UpdateFirmwareTab = ({ onSuccessRefresh }) => {
  const [appName, setAppName] = useState('');
  const [appVersion, setAppVersion] = useState('');
  const [file, setFile] = useState(null);
  const [apps, setApps] = useState([]);
  const [appsLoading, setAppsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmData, setConfirmData] = useState(null);

  const loadApps = useCallback(async () => {
    setAppsLoading(true);
    try {
      const r = await apiClient.get('/api/applications');
      setApps(r.data.applications || []);
    } catch (e) {
      toast.warn(toasterFromError(e, 'Could not load existing applications for autocomplete'));
    } finally {
      setAppsLoading(false);
    }
  }, []);

  useEffect(() => { loadApps(); }, [loadApps]);

  const appNameExact = useMemo(() => apps.find(a => String(a.name) === String(appName).trim()), [apps, appName]);
  const isNewApp = appName.trim().length > 0 && !appNameExact;
  const existingVersions = appNameExact ? (appNameExact.versions || []) : [];
  const currentVersion = appNameExact ? appNameExact.current_version : null;
  const versionExists = appNameExact && appVersion.trim().length > 0
    ? existingVersions.some(v => String(v) === String(appVersion).trim())
    : false;
  const versionIsNew = !versionExists && appVersion.trim().length > 0 && /^\d+\.\d+\.\d+$/.test(appVersion.trim());

  const fileExtOk = !!file && /\.bin$/i.test(file.name);
  const fileNameMatches = !(appName.trim() && file) || (file.name.replace(/\.bin$/i, '').toLowerCase() === appName.trim().toLowerCase());

  const canSubmit = !!appName.trim() && !!appVersion.trim() && /^\d+\.\d+\.\d+$/.test(appVersion.trim())
    && !!file && fileExtOk && !versionExists && !submitting;

  const handleSubmitClick = (e) => {
    if (e) e.preventDefault();
    if (!canSubmit) {
      if (versionExists) toast.warn(`Version ${appVersion} already exists for ${appName}.`);
      else if (!fileExtOk) toast.warn('File must be of type .bin.');
      else if (!fileNameMatches) toast.warn('Application name and Binary File name should be the same.');
      else toast.warn('Please fill in all required fields correctly.');
      return;
    }
    setConfirmData({
      appName: appName.trim(),
      appVersion: appVersion.trim(),
      fileName: file.name,
      fileSize: file.size,
      current_version: currentVersion
    });
  };

  const doSubmit = async () => {
    if (!confirmData) return;
    setSubmitting(true);
    const formData = new FormData();
    formData.append('appName', confirmData.appName);
    formData.append('appVersion', confirmData.appVersion);
    formData.append('binaryFile', file);
    formData.append('fileName', file.name);
    try {
      const pendingToast = toast.loading('Submitting OTA request…', { autoClose: false });
      const response = await axios({
        method: 'post',
        baseURL: '/updateFirm',
        headers: { 'Content-Type': 'multipart/form-data' },
        data: formData,
        timeout: 120000
      });
      toast.update(pendingToast, {
        render: response.data && response.data.message ? response.data.message : 'OTA update submitted successfully',
        type: 'success', isLoading: false, autoClose: 4000, theme: 'colored'
      });
      setConfirmData(null);
      setAppName(''); setAppVersion(''); setFile(null);
      loadApps();
      if (onSuccessRefresh) setTimeout(() => onSuccessRefresh(), 500);
    } catch (error) {
      const msg = toasterFromError(error, 'OTA update failed');
      toast.error(msg, { position: 'top-right', theme: 'colored' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="update-form">
      <h2>
        <i className="fa fa-cloud-upload-alt" style={{ color: '#0DB8DE', marginRight: 10 }} aria-hidden="true"></i>
        Update Firmware
      </h2>

      <form onSubmit={handleSubmitClick}>
        <div className="form-group">
          <label htmlFor="uf-app-name">Application Name</label>
          <input
            list="uf-apps-list"
            type="text"
            id="uf-app-name"
            className="form-control"
            placeholder="Enter application name or pick existing"
            value={appName}
            onChange={(e) => setAppName(e.target.value)}
            autoComplete="off"
            required
          />
          <datalist id="uf-apps-list">
            {apps.map(a => <option key={a.name} value={a.name} />)}
          </datalist>
          {appsLoading && <small style={{ color: '#8a9396', fontSize: 12 }}>Loading existing applications…</small>}
          {appNameExact && (
            <>
              <div className="info-alert" style={{ marginTop: 10 }}>
                <i className="fa fa-info-circle" style={{ marginRight: 6 }} aria-hidden="true"></i>
                Existing Application — Current version: <strong style={{ color: '#27EF9F' }}>{appNameExact.current_version}</strong>
              </div>
              <div style={{ fontSize: 12, color: '#8a9396', marginTop: 6, marginBottom: 2 }}>Existing Versions ({existingVersions.length}):</div>
              <div className="existing-versions-chips">
                {existingVersions.map((v, i) => (
                  <span key={v + '_' + i} className={`version-chip ${i === 0 ? 'current' : ''}`}>
                    {v}{i === 0 && ' (current)'}
                  </span>
                ))}
              </div>
            </>
          )}
          {isNewApp && (
            <div className="info-alert">
              <i className="fa fa-plus-circle" style={{ marginRight: 6 }} aria-hidden="true"></i>
              <strong>New Application</strong> — This application does not currently exist. A new entry will be created.
            </div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="uf-app-version">Application Version</label>
          <input
            type="text"
            id="uf-app-version"
            className="form-control"
            placeholder="e.g. 1.0.5"
            pattern="\d+\.\d+\.\d+"
            title="Must match X.Y.Z semver (e.g. 1.0.5)"
            value={appVersion}
            onChange={(e) => setAppVersion(e.target.value)}
            required
          />
          {versionExists && (
            <div className="warn-alert">
              <i className="fa fa-exclamation-triangle" style={{ marginRight: 6 }} aria-hidden="true"></i>
              Version <strong>{appVersion}</strong> already exists for {appNameExact ? appNameExact.name : appName}. Choose a higher version number.
            </div>
          )}
          {versionIsNew && (
            <div className="success-alert">
              <i className="fa fa-check-circle" style={{ marginRight: 6 }} aria-hidden="true"></i>
              New version <strong>{appVersion}</strong> detected.
              {currentVersion && ` (current: ${currentVersion})`}
            </div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="uf-binary">Firmware Binary (.bin)</label><br />
          <input
            type="file"
            id="uf-binary"
            className="form-control-file"
            name="binaryFile"
            accept=".bin"
            onChange={(e) => setFile(e.target.files ? e.target.files[0] : null)}
            required
          />
          {file && (
            <div style={{ marginTop: 8, fontSize: 12, color: '#b0b0b0' }}>
              <i className="fa fa-file" style={{ marginRight: 6 }} aria-hidden="true"></i>
              {file.name} &nbsp; <span style={{ color: '#8a9396' }}>— {(file.size / 1024).toFixed(1)} KB</span>
            </div>
          )}
          {!fileExtOk && file && (
            <div className="warn-alert">⚠ File must be a .bin binary.</div>
          )}
          {fileExtOk && !fileNameMatches && (
            <div className="warn-alert">
              ⚠ Application name (“{appName}”) should match the binary file name (“{file.name.replace(/\.bin$/i,'')}”).
            </div>
          )}
        </div>

        <div className="mt-3">
          <button
            type="submit"
            className="btn-submit"
            disabled={!canSubmit || submitting}
          >
            {submitting ? (
              <><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ marginRight: 8 }}></span>Submitting OTA…</>
            ) : (
              <><i className="fa fa-cloud-upload-alt" style={{ marginRight: 8 }} aria-hidden="true"></i>Upload / Submit OTA</>
            )}
          </button>
        </div>
      </form>

      <ConfirmationModal
        data={confirmData}
        onCancel={() => setConfirmData(null)}
        onSubmit={doSubmit}
      />
    </div>
  );
};

export default UpdateFirmwareTab;
